import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');

const PERIODS = [
    { label: 'Today',      value: 'today'      },
    { label: 'This Week',  value: 'week'       },
    { label: 'This Month', value: 'thisMonth'  },
    { label: 'Last Month', value: 'lastMonth'  },
    { label: 'This Year',  value: 'thisYear'   },
    { label: 'All Time',   value: 'allTime'    },
];

function periodRange(p) {
    const now = new Date();
    const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    const todayStr = `${y}-${pad(mo + 1)}-${pad(d)}`;

    if (p === 'today')     return { from: todayStr, to: todayStr };
    if (p === 'week')      { const sun = new Date(now); sun.setDate(d - now.getDay()); return { from: iso(sun), to: todayStr }; }
    if (p === 'thisMonth') return { from: `${y}-${pad(mo + 1)}-01`, to: todayStr };
    if (p === 'lastMonth') { const lm = new Date(y, mo, 0); return { from: `${lm.getFullYear()}-${pad(lm.getMonth() + 1)}-01`, to: iso(lm) }; }
    if (p === 'thisYear')  return { from: `${y}-01-01`, to: todayStr };
    return { from: '2000-01-01', to: todayStr };
}

const monthLabel = (isoDate) => {
    const [y, m] = isoDate.split('-');
    return new Date(y, parseInt(m) - 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};

export default function AccountsProfitLoss() {
    const [period, setPeriod]   = useState('thisMonth');
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView]       = useState('summary');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/receipt/');
            const all = Array.isArray(res.data) ? res.data : [];
            const { from, to } = periodRange(period);
            setRows(all.filter(r => {
                const ds = (r.createdAt || '').toString().substring(0, 10);
                return ds >= from && ds <= to;
            }));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const totalEmi     = rows.reduce((s, r) => s + (parseFloat(r.emi_amount)       || 0), 0);
    const totalPenalty = rows.reduce((s, r) => s + (parseFloat(r.penalty_amount)   || 0), 0);
    const totalFine    = rows.reduce((s, r) => s + (parseFloat(r.fine_amount)      || 0), 0);
    const totalGross   = rows.reduce((s, r) => s + (parseFloat(r.total)            || 0), 0);

    const byMonth = rows.reduce((acc, r) => {
        const month = (r.createdAt || '').toString().substring(0, 7);
        if (!month) return acc;
        if (!acc[month]) acc[month] = { emi: 0, penalty: 0, fine: 0, total: 0, count: 0 };
        acc[month].emi     += parseFloat(r.emi_amount)     || 0;
        acc[month].penalty += parseFloat(r.penalty_amount) || 0;
        acc[month].fine    += parseFloat(r.fine_amount)    || 0;
        acc[month].total   += parseFloat(r.total)          || 0;
        acc[month].count++;
        return acc;
    }, {});
    const monthKeys = Object.keys(byMonth).sort();

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-chart-line text-purple mr-2" style={{ color: '#6610f2' }}></i>Profit & Loss
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/accounts">Accounts</a></li>
                                <li className="breadcrumb-item active">Profit & Loss</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {/* Controls */}
                    <div className="card mb-3">
                        <div className="card-body py-2">
                            <div className="row align-items-center">
                                <div className="col-md-8 mb-2 mb-md-0">
                                    <div className="btn-group btn-group-sm flex-wrap">
                                        {PERIODS.map(p => (
                                            <button key={p.value} className={`btn ${period === p.value ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPeriod(p.value)}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-md-4 text-right">
                                    <div className="btn-group btn-group-sm">
                                        <button className={`btn ${view === 'summary' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setView('summary')}>Summary</button>
                                        <button className={`btn ${view === 'monthly' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setView('monthly')}>By Month</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                    ) : (
                        <>
                            {/* P&L Summary */}
                            <div className="row mb-3">
                                <div className="col-md-3 col-sm-6 mb-2">
                                    <div className="callout callout-info">
                                        <h6 className="text-muted mb-0">EMI Income</h6>
                                        <p className="h3 font-weight-bold text-info mb-0">{fmt(totalEmi)}</p>
                                    </div>
                                </div>
                                <div className="col-md-3 col-sm-6 mb-2">
                                    <div className="callout callout-warning">
                                        <h6 className="text-muted mb-0">Penalty</h6>
                                        <p className="h3 font-weight-bold text-warning mb-0">{fmt(totalPenalty)}</p>
                                    </div>
                                </div>
                                <div className="col-md-3 col-sm-6 mb-2">
                                    <div className="callout callout-danger">
                                        <h6 className="text-muted mb-0">Late Fine</h6>
                                        <p className="h3 font-weight-bold text-danger mb-0">{fmt(totalFine)}</p>
                                    </div>
                                </div>
                                <div className="col-md-3 col-sm-6 mb-2">
                                    <div className="callout callout-success">
                                        <h6 className="text-muted mb-0">Gross Revenue</h6>
                                        <p className="h3 font-weight-bold text-success mb-0">{fmt(totalGross)}</p>
                                    </div>
                                </div>
                            </div>

                            {view === 'summary' ? (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Income Breakdown ({rows.length} receipts)</h3>
                                    </div>
                                    <div className="card-body">
                                        {totalGross === 0 ? (
                                            <div className="text-center text-muted py-4">
                                                <i className="fas fa-inbox fa-3x mb-3 d-block"></i>No revenue in this period
                                            </div>
                                        ) : (
                                            <>
                                                {[
                                                    { label: 'EMI Income',  value: totalEmi,     pct: totalEmi     / totalGross * 100, color: '#17a2b8' },
                                                    { label: 'Penalty',     value: totalPenalty, pct: totalPenalty / totalGross * 100, color: '#ffc107' },
                                                    { label: 'Late Fine',   value: totalFine,    pct: totalFine    / totalGross * 100, color: '#dc3545' },
                                                ].map(item => (
                                                    <div key={item.label} className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="font-weight-bold">{item.label}</span>
                                                            <span>{fmt(item.value)} ({item.pct.toFixed(1)}%)</span>
                                                        </div>
                                                        <div className="progress" style={{ height: 10 }}>
                                                            <div className="progress-bar" style={{ width: `${item.pct}%`, backgroundColor: item.color }}></div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="card">
                                    <div className="card-body p-0">
                                        {monthKeys.length === 0 ? (
                                            <div className="text-center py-5 text-muted">No data for this period</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover table-sm mb-0">
                                                    <thead className="thead-light">
                                                        <tr><th>Month</th><th>Receipts</th><th>EMI</th><th>Penalty</th><th>Late Fine</th><th>Gross Total</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {monthKeys.map(mo => {
                                                            const m = byMonth[mo];
                                                            return (
                                                                <tr key={mo}>
                                                                    <td className="font-weight-bold">{monthLabel(mo)}</td>
                                                                    <td>{m.count}</td>
                                                                    <td>{fmt(m.emi)}</td>
                                                                    <td>{fmt(m.penalty)}</td>
                                                                    <td>{fmt(m.fine)}</td>
                                                                    <td className="font-weight-bold text-success">{fmt(m.total)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot className="thead-light">
                                                        <tr>
                                                            <td><strong>Total</strong></td>
                                                            <td>{rows.length}</td>
                                                            <td><strong>{fmt(totalEmi)}</strong></td>
                                                            <td><strong>{fmt(totalPenalty)}</strong></td>
                                                            <td><strong>{fmt(totalFine)}</strong></td>
                                                            <td><strong className="text-success">{fmt(totalGross)}</strong></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
